import { z } from "zod";

import { lineStatusesSchema } from "@/api/contracts/spec-editor";
import { SPEC_PUSH_COMMUNITY_IDS_MAX } from "@/lib/spec-push-limits";

import {
  SPEC_FILTER_BLANK_SENTINEL,
  parseSpecListFilters,
  parseSpecListSort,
  serializeSpecListSort,
  type SpecListUrlFilter,
  type SpecListUrlSort,
} from "@/lib/spec-list-url-state";

export type { SpecListUrlFilter };

// The browser seam for the Spec list (#689): what the page may ask the BFF to
// fetch, the vocabularies its controls offer, and the map between the source's
// URL encoding and the wire's names. The wire faces for
// `/internal/specifications/*` are the other seam and live on their `endpoint()`
// declarations (`@/server/data/internal-api/specifications`) — see
// `client/docs/architecture.md` L2, "Two seams, two homes for a schema".
//
// Two vocabularies sit side by side here, and they are not the same list:
//
//   - **What the server accepts** per tab (`SPEC_LIST_SORT_FIELDS_BY_TAB`,
//     `SPEC_LIST_FILTER_KINDS_BY_TAB`, …) — TIM's allow-lists in Atlas words,
//     pinned to Python's use case by `spec-list-rules-drift.unit.test.ts`.
//   - **What the page offers** (`SPEC_LIST_SORTABLE_FIELDS`,
//     `SPEC_LIST_FILTERABLE_COLUMNS`) — Timmy's per-tab constants, labels and
//     order included (`SpecsListTableIsland.tsx:74-225`), a subset of the first:
//     no tab offers `createdAt` (there is no Created column; it is only the
//     server default).
//
// The page tickets (#691, #692) parse `?sort=` and `?filters=` in the source's
// URL encoding — Django field names, and the `(Blank)` sentinel — so a modsy
// link still resolves; the maps below turn those into wire names and a
// structured `includeBlank`, and back. They admit only what the *view offers*,
// as Timmy's parsers do: a `?sort=created_at` is dropped, and the table falls
// back to its default.

export const SPEC_TYPES = ["core", "base", "community"] as const;
export type SpecType = (typeof SPEC_TYPES)[number];

/** A spec type's word in the source's titles and copy: "Core Specifications List". */
export const SPEC_TYPE_LABEL = {
  core: "Core",
  base: "Base",
  community: "Community",
} as const satisfies Record<SpecType, string>;

export const SPEC_LIST_STATUSES = ["active", "archived", "all"] as const;
export type SpecListStatus = (typeof SPEC_LIST_STATUSES)[number];

/**
 * The Base tab's link-status filter (#1085), Core Spec Links' Link Status: a
 * base with a core Parent version, or without one. Absent is All.
 */
export const SPEC_CORE_LINKS = ["linked", "unlinked"] as const;
export type SpecCoreLink = (typeof SPEC_CORE_LINKS)[number];

/** A table the list renders: one of the three tabs, or the trimmed
 *  single-community table on a community's page. */
export type SpecListView = SpecType | "singleCommunity";

/** The Spec list page's page-size menu (`pages/spec-list-page.tsx:42-43`). */
export const SPEC_LIST_PAGE_SIZES = [10, 20, 50] as const;
export const SPEC_LIST_DEFAULT_PAGE_SIZE = 20;

/** The single-community table's menu: the island's own defaults, which the
 *  community details page does not override (`SpecsListTableIsland.tsx:262-263`). */
export const SINGLE_COMMUNITY_PAGE_SIZES = [5, 10, 20] as const;
export const SINGLE_COMMUNITY_DEFAULT_PAGE_SIZE = 10;

/** The Linked Specs popover's one page (`linked-specs/actions.ts:57-65`). */
export const LINKED_SPECS_PAGE_SIZE = 100;

/** Python's shared page-size ceiling (`app/use_cases/paging.py`), pinned. */
export const SPEC_LIST_MAX_PAGE_SIZE = 100;

/** Distinct values: the source's `limit` default and ceiling, pinned. */
export const SPEC_DISTINCT_VALUES_DEFAULT_LIMIT = 50;
export const SPEC_DISTINCT_VALUES_MAX_LIMIT = 200;

export const SPEC_LIST_SORT_FIELDS = [
  "name",
  "createdAt",
  "communityId",
  "lastPublishedAt",
  "effectiveAt",
  "divisionName",
  "parentSpecName",
  "linkedCount",
] as const;
export type SpecListSortField = (typeof SPEC_LIST_SORT_FIELDS)[number];

export const SPEC_LIST_FILTER_FIELDS = [
  "name",
  "communityId",
  "parentSpecName",
  "lastPublishedAt",
  "effectiveAt",
  "linkedCount",
] as const;
export type SpecListFilterField = (typeof SPEC_LIST_FILTER_FIELDS)[number];

export const SPEC_LIST_FILTER_OPERATORS = ["icontains", "istartswith", "in", "gte", "lte"] as const;
export type SpecListFilterOperator = (typeof SPEC_LIST_FILTER_OPERATORS)[number];

export const SPEC_LIST_FILTER_KINDS = ["text", "date", "multiSelect", "linkedCount"] as const;
export type SpecListFilterKind = (typeof SPEC_LIST_FILTER_KINDS)[number];

export const SPEC_DISTINCT_COLUMNS = ["name", "communityId", "parentSpecName"] as const;
export type SpecDistinctColumn = (typeof SPEC_DISTINCT_COLUMNS)[number];

export type SortDirection = "asc" | "desc";

export interface SpecListSortItem {
  field: SpecListSortField;
  direction: SortDirection;
}

// --- what the server accepts, per tab (pinned to Python) --------------------

/** Each tab's sort fields (`list_specs.SORT_FIELDS`). */
export const SPEC_LIST_SORT_FIELDS_BY_TAB: Record<SpecType, readonly SpecListSortField[]> = {
  core: ["name", "createdAt", "lastPublishedAt", "effectiveAt", "linkedCount"],
  base: [
    "name",
    "createdAt",
    "lastPublishedAt",
    "effectiveAt",
    "divisionName",
    "parentSpecName",
    "linkedCount",
  ],
  community: [
    "name",
    "createdAt",
    "communityId",
    "lastPublishedAt",
    "effectiveAt",
    "divisionName",
    "parentSpecName",
  ],
};

/** Each tab's filterable fields and their kind (`list_specs.FILTER_FIELDS`). The
 *  Base tab's community-count filter is absent, as the source withheld it. */
export const SPEC_LIST_FILTER_KINDS_BY_TAB: Record<
  SpecType,
  Partial<Record<SpecListFilterField, SpecListFilterKind>>
> = {
  core: {
    name: "text",
    lastPublishedAt: "date",
    effectiveAt: "date",
    linkedCount: "linkedCount",
  },
  base: {
    name: "text",
    lastPublishedAt: "date",
    effectiveAt: "date",
    parentSpecName: "multiSelect",
  },
  community: {
    communityId: "multiSelect",
    name: "text",
    lastPublishedAt: "date",
    effectiveAt: "date",
    parentSpecName: "multiSelect",
  },
};

/** The operators each kind takes (`list_specs.FILTER_OPERATORS`). */
export const SPEC_LIST_FILTER_OPERATORS_BY_KIND: Record<
  SpecListFilterKind,
  readonly SpecListFilterOperator[]
> = {
  text: ["icontains", "istartswith", "in"],
  date: ["gte", "lte"],
  multiSelect: ["in"],
  linkedCount: ["gte", "lte"],
};

/** Each tab's distinct-values columns (`list_distinct_values.DISTINCT_COLUMNS`). */
export const SPEC_DISTINCT_COLUMNS_BY_TAB: Record<SpecType, readonly SpecDistinctColumn[]> = {
  core: ["name"],
  base: ["name", "parentSpecName"],
  community: ["name", "communityId", "parentSpecName"],
};

/** The order the server applies when a request names none: TIM's `-created_at`. */
export const SPEC_LIST_SERVER_DEFAULT_SORT: readonly SpecListSortItem[] = [
  { field: "createdAt", direction: "desc" },
];

// --- what the page offers (Timmy's constants) ----------------------------------

export interface SpecListSortableField {
  field: SpecListSortField;
  label: string;
}

/** The sort popover's fields per table, in the order and with the labels the
 *  source's popover shows (`SpecsListTableIsland.tsx:74-97, 136-141`). */
export const SPEC_LIST_SORTABLE_FIELDS: Record<SpecListView, readonly SpecListSortableField[]> = {
  core: [
    { field: "name", label: "Name" },
    { field: "lastPublishedAt", label: "Last Publish Date" },
    { field: "effectiveAt", label: "Effective Date" },
    { field: "linkedCount", label: "Base Specification" },
  ],
  base: [
    { field: "name", label: "Name" },
    { field: "lastPublishedAt", label: "Last Publish Date" },
    { field: "effectiveAt", label: "Effective Date" },
    { field: "linkedCount", label: "Communities" },
    { field: "parentSpecName", label: "Core Specification" },
    { field: "divisionName", label: "Division" },
  ],
  community: [
    { field: "communityId", label: "Community ID" },
    { field: "name", label: "Specification" },
    { field: "lastPublishedAt", label: "Last Publish Date" },
    { field: "effectiveAt", label: "Effective Date" },
    { field: "parentSpecName", label: "Based On" },
    { field: "divisionName", label: "Division" },
  ],
  singleCommunity: [
    { field: "name", label: "Specification" },
    { field: "lastPublishedAt", label: "Last Publish Date" },
    { field: "effectiveAt", label: "Effective Date" },
    { field: "parentSpecName", label: "Based On" },
  ],
};

/**
 * The sort a table sends when the URL carries none (`SpecsListTableIsland.tsx:112-116`).
 * Core sends `name` ascending explicitly; the others send nothing and take the
 * server default. An explicit clear (`?sort=`) sends nothing on every tab, so a
 * cleared Core tab is newest first, as the source's is (`useSpecsSortState.ts:112-116`).
 */
export const SPEC_LIST_PAGE_DEFAULT_SORT: Record<
  SpecListView,
  readonly SpecListSortItem[] | undefined
> = {
  core: [{ field: "name", direction: "asc" }],
  base: undefined,
  community: undefined,
  singleCommunity: undefined,
};

/** The sort trigger's label while a table relies on the server default. */
export const SPEC_LIST_DEFAULT_SORT_LABEL: Record<SpecListView, string | undefined> = {
  core: undefined,
  base: "Newest first",
  community: "Newest first",
  singleCommunity: undefined,
};

export interface SpecListFilterableColumn {
  field: SpecListFilterField;
  label: string;
  kind: SpecListFilterKind;
  /**
   * Where the heading and its funnel sit in the header — the source keeps it
   * on the filter definition (`align: 'right'`), and it is the funnel row's
   * `justify-content`, not the cell's alignment.
   */
  align?: "right";
}

/** The column filters per table, with the source's labels and kinds
 *  (`SpecsListTableIsland.tsx:175-225`). */
export const SPEC_LIST_FILTERABLE_COLUMNS: Record<
  SpecListView,
  readonly SpecListFilterableColumn[]
> = {
  core: [
    { field: "name", label: "Name", kind: "text" },
    { field: "lastPublishedAt", label: "Last Publish Date", kind: "date" },
    { field: "effectiveAt", label: "Effective Date", kind: "date", align: "right" },
    { field: "linkedCount", label: "Base Specification", kind: "linkedCount", align: "right" },
  ],
  base: [
    { field: "name", label: "Name", kind: "text" },
    { field: "lastPublishedAt", label: "Last Publish Date", kind: "date" },
    { field: "effectiveAt", label: "Effective Date", kind: "date", align: "right" },
    { field: "parentSpecName", label: "Core Specification", kind: "multiSelect" },
  ],
  community: [
    { field: "communityId", label: "Community ID", kind: "multiSelect" },
    { field: "name", label: "Specification", kind: "text" },
    { field: "lastPublishedAt", label: "Last Publish Date", kind: "date" },
    { field: "effectiveAt", label: "Effective Date", kind: "date", align: "right" },
    { field: "parentSpecName", label: "Based On", kind: "multiSelect", align: "right" },
  ],
  singleCommunity: [
    { field: "name", label: "Specification", kind: "text" },
    { field: "lastPublishedAt", label: "Last Publish Date", kind: "date" },
    { field: "effectiveAt", label: "Effective Date", kind: "date", align: "right" },
    { field: "parentSpecName", label: "Based On", kind: "multiSelect", align: "right" },
  ],
};

// --- the source's URL encoding --------------------------------------------------

/**
 * Each tab's source URL field names (TIM's Django names, as `?sort=` and
 * `?filters=` carry them) onto the wire's — the names Timmy's views offer, no
 * more. Per tab, because two wire names stand for different source fields on
 * different tabs: `linkedCount` is `base_specifications_count` on Core and
 * `community_specifications_count` on Base, and `parentSpecName` is
 * `based_on_core__specification__name` on Base and
 * `based_on__specification__name` on Community.
 */
// Every filter field is also a sort field, so one value type serves both maps.
export const SPEC_LIST_URL_FIELDS: Record<SpecType, Readonly<Record<string, SpecListSortField>>> = {
  core: {
    name: "name",
    last_published_at: "lastPublishedAt",
    effective_at: "effectiveAt",
    base_specifications_count: "linkedCount",
  },
  base: {
    name: "name",
    last_published_at: "lastPublishedAt",
    effective_at: "effectiveAt",
    division__name: "divisionName",
    based_on_core__specification__name: "parentSpecName",
    community_specifications_count: "linkedCount",
  },
  community: {
    name: "name",
    community_id: "communityId",
    last_published_at: "lastPublishedAt",
    effective_at: "effectiveAt",
    division__name: "divisionName",
    based_on__specification__name: "parentSpecName",
  },
};

/** The tab whose names and rules a view uses: the single-community table is
 *  the Community tab trimmed. */
export const specListViewTab = (view: SpecListView): SpecType =>
  view === "singleCommunity" ? "community" : view;

function urlFieldName(view: SpecListView, urlField: string): SpecListSortField | undefined {
  const names = SPEC_LIST_URL_FIELDS[specListViewTab(view)];
  return Object.hasOwn(names, urlField) ? names[urlField] : undefined;
}

/** A `?sort=` field as the wire names it, or `undefined` when `view` does not
 *  offer it — the page drops it, as Timmy's `useSpecsSortState` does. */
export function urlFieldToWire(
  view: SpecListView,
  urlField: string,
): SpecListSortField | undefined {
  const field = urlFieldName(view, urlField);
  return SPEC_LIST_SORTABLE_FIELDS[view].some((offered) => offered.field === field)
    ? field
    : undefined;
}

/** The inverse: a wire field as the source's URL spells it on `view`. */
export function wireFieldToUrl(view: SpecListView, field: SpecListSortField): string | undefined {
  return Object.entries(SPEC_LIST_URL_FIELDS[specListViewTab(view)]).find(
    ([, wire]) => wire === field,
  )?.[0];
}

/**
 * The sort a table runs on, from its raw `?sort=` (`useSpecsSortState.ts:
 * 92-116`):
 *
 *   - **absent** takes the view's page default — Core's Name A→Z, nothing on
 *     the others, which the server orders newest first;
 *   - **anything else** is the parsed list with every field the view does not
 *     offer dropped. That includes the explicit empty `?sort=` Clear all
 *     writes, and — the source's own quirk, carried — a sort of nothing the
 *     view offers (`?sort=bogus`), which is no sort rather than the default.
 */
export function effectiveSpecListSort(
  view: SpecListView,
  rawSort: string | undefined,
): SpecListSortItem[] {
  const parsed = parseSpecListSort(rawSort);
  if (parsed === null) return [...(SPEC_LIST_PAGE_DEFAULT_SORT[view] ?? [])];
  return parsed.flatMap(({ field, direction }) => {
    const wire = urlFieldToWire(view, field);
    return wire === undefined ? [] : [{ field: wire, direction }];
  });
}

/** The inverse, for a sort popover's write: `""` for no sort, the sentinel. */
export function specListSortToUrl(view: SpecListView, sort: readonly SpecListSortItem[]): string {
  return serializeSpecListSort(
    sort.flatMap(({ field, direction }): SpecListUrlSort[] => {
      const url = wireFieldToUrl(view, field);
      return url === undefined ? [] : [{ field: url, direction }];
    }),
  );
}

/**
 * The list route's search params: `?sort=` and `?filters=` in the source's raw
 * encodings, and nothing else. Page, page size, search text and Show are the
 * page's state, as the source keeps them, so a link copied between the two
 * stacks carries the same params. Anything unreadable drops to absent rather
 * than failing the navigation. `filters` reaches here as the raw JSON text —
 * the app's search codec reads it literally (`router-search-codec.ts`), so a
 * value TanStack would otherwise have parsed into a list never arrives as one.
 */
export const specListSearchSchema = z.object({
  sort: z.string().optional().catch(undefined),
  filters: z.string().optional().catch(undefined),
});

export type SpecListSearch = z.infer<typeof specListSearchSchema>;

// The `(Blank)` sentinel is a URL-encoding fact, so it lives beside the parser
// in `lib/`; it is re-exported here for the callers that already reach for it.
// It never reaches the wire — `includeBlank` does.
export { SPEC_FILTER_BLANK_SENTINEL };

export interface SpecListFilterInput {
  field: SpecListFilterField;
  operator: SpecListFilterOperator;
  values: string[];
  includeBlank?: boolean;
}

/**
 * A URL filter as the wire takes it, or `undefined` when the page would drop
 * it, as Timmy does (`parseColumnFilters`, `useSpecsColumnFiltersState`):
 *
 *   - empty-string values are dropped, and a filter left with none is too;
 *   - a column `view` does not offer is dropped;
 *   - an operator the column's kind does not take is dropped — Timmy sends it,
 *     and Falcon ignores the parameter it does not know;
 *   - a single-value operator carries its first value only (`values[0]`).
 *
 * The sentinel leaves `values` and becomes `includeBlank` on an `in`.
 */
export function urlFilterToWire(
  view: SpecListView,
  filter: SpecListUrlFilter,
): SpecListFilterInput | undefined {
  const values = filter.values.filter((value) => value.length > 0);
  if (values.length === 0) return undefined;
  const name = urlFieldName(view, filter.field);
  const column = SPEC_LIST_FILTERABLE_COLUMNS[view].find((offered) => offered.field === name);
  if (column === undefined) return undefined;
  const { field, kind } = column;
  const operator = SPEC_LIST_FILTER_OPERATORS_BY_KIND[kind].find((op) => op === filter.operator);
  if (operator === undefined) return undefined;
  if (operator !== "in") return { field, operator, values: [values[0]!] };
  const includeBlank = values.includes(SPEC_FILTER_BLANK_SENTINEL);
  return {
    field,
    operator,
    values: values.filter((value) => value !== SPEC_FILTER_BLANK_SENTINEL),
    ...(includeBlank ? { includeBlank } : {}),
  };
}

/**
 * A whole `?filters=` list as the wire takes it. A second filter on the same
 * source field and operator is a duplicate and dropped, the first standing, as
 * Timmy's parser does — before the view's columns are consulted, and counting
 * only filters that kept a value.
 */
export function urlFiltersToWire(
  view: SpecListView,
  filters: readonly SpecListUrlFilter[],
): SpecListFilterInput[] {
  const seen = new Set<string>();
  const kept: SpecListFilterInput[] = [];
  for (const filter of filters) {
    const key = `${filter.field}|${filter.operator}`;
    if (seen.has(key) || !filter.values.some((value) => value.length > 0)) continue;
    seen.add(key);
    const wire = urlFilterToWire(view, filter);
    if (wire !== undefined) kept.push(wire);
  }
  return kept;
}

/** The inverse, for writing a filter back into the URL the source's way. */
export function wireFilterToUrl(
  view: SpecListView,
  filter: SpecListFilterInput,
): SpecListUrlFilter | undefined {
  const field = wireFieldToUrl(view, filter.field);
  if (field === undefined) return undefined;
  return {
    field,
    operator: filter.operator,
    values: filter.includeBlank
      ? [SPEC_FILTER_BLANK_SENTINEL, ...filter.values]
      : [...filter.values],
  };
}

/** The source URL field names a view's filters may use — its allow-list. */
/**
 * The view's filter on a list column, and the field the URL names it by — or
 * nothing where the view offers that column no filter.
 */
export function specListFilterableColumn(
  view: SpecListView,
  field: string,
): { column: SpecListFilterableColumn; urlField: string } | undefined {
  const column = SPEC_LIST_FILTERABLE_COLUMNS[view].find((candidate) => candidate.field === field);
  const urlField = column && wireFieldToUrl(view, column.field);
  return column && urlField !== undefined ? { column, urlField } : undefined;
}

function filterUrlFields(view: SpecListView): ReadonlySet<string> {
  return new Set(
    SPEC_LIST_FILTERABLE_COLUMNS[view].flatMap(({ field }) => wireFieldToUrl(view, field) ?? []),
  );
}

/**
 * A table's column filters, from its raw `?filters=`, as the source's hook
 * holds them (`useSpecsColumnFiltersState`'s `filtersByField`): parsed, every
 * field the view does not offer dropped, and each field's filters grouped
 * together, fields in the order they first appear. Still in the URL's own
 * words — the popovers edit these and the write rebuilds `?filters=` from
 * them, so what a table reads is exactly what its next write keeps.
 *
 * A filter whose operator the column's kind does not take stays: it marks the
 * column active and counts, as the source's does, and only the wire drops it.
 */
export function specListUrlFilters(
  view: SpecListView,
  rawFilters: string | undefined,
): SpecListUrlFilter[] {
  const allowed = filterUrlFields(view);
  const byField = new Map<string, SpecListUrlFilter[]>();
  for (const filter of parseSpecListFilters(rawFilters) ?? []) {
    if (!allowed.has(filter.field)) continue;
    byField.set(filter.field, [...(byField.get(filter.field) ?? []), filter]);
  }
  return [...byField.values()].flat();
}

/**
 * One column's Apply or Clear (`setColumnFilters`): every filter on `field`
 * replaced by `next` — each forced onto `field`, an empty one dropped — and
 * the column moved to the **end** of the list, which is where the source's
 * write puts it. A column the view does not offer changes nothing.
 */
export function specListFilterColumnWrite(
  view: SpecListView,
  current: readonly SpecListUrlFilter[],
  field: string,
  next: readonly SpecListUrlFilter[],
): SpecListUrlFilter[] {
  if (!filterUrlFields(view).has(field)) return [...current];
  const cleaned = next
    .filter((filter) => filter.values.length > 0)
    .map((filter) => ({ ...filter, field }));
  return [...current.filter((filter) => filter.field !== field), ...cleaned];
}

/** How many columns carry a filter — `activeColumnCount`: a date range is one. */
export function specListActiveFilterColumns(filters: readonly SpecListUrlFilter[]): number {
  return new Set(filters.map((filter) => filter.field)).size;
}

// --- server-function inputs ------------------------------------------------------

const specListSortInput = z.strictObject({
  field: z.enum(SPEC_LIST_SORT_FIELDS),
  direction: z.enum(["asc", "desc"]),
});

const specListFilterInput = z.strictObject({
  field: z.enum(SPEC_LIST_FILTER_FIELDS),
  operator: z.enum(SPEC_LIST_FILTER_OPERATORS),
  values: z.array(z.string()),
  includeBlank: z.boolean().optional(),
});

/**
 * The Spec list read's input, browser seam: the caller's list query.
 *
 * Strict, and every optional field value-or-absent rather than nullable — a
 * `null` arriving here would mean a caller that is not the page. The per-tab
 * rules (which fields a tab sorts and filters by) are Python's to enforce and
 * are not restated as a validator: a refusal there is a typed `invalid-input`,
 * where a rejection here is scrubbed to a generic message and explains nothing.
 * The bounds below are the ones no page can produce past, mirrored so a bad
 * caller fails before the round trip.
 */
export const specListQueryInputSchema = z.strictObject({
  specType: z.enum(SPEC_TYPES),
  status: z.enum(SPEC_LIST_STATUSES),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1).max(SPEC_LIST_MAX_PAGE_SIZE),
  sort: z.array(specListSortInput).optional(),
  divisionId: z.number().int().optional(),
  search: z.string().optional(),
  internalCommunityId: z.number().int().min(1).optional(),
  parentSpecId: z.number().int().optional(),
  filters: z.array(specListFilterInput).optional(),
  coreLink: z.enum(SPEC_CORE_LINKS).optional(),
});

export type SpecListQueryBrowserInput = z.infer<typeof specListQueryInputSchema>;

/** The distinct-values read's input, browser seam. */
export const specDistinctValuesInputSchema = z.strictObject({
  specType: z.enum(SPEC_TYPES),
  column: z.enum(SPEC_DISTINCT_COLUMNS),
  divisionId: z.number().int().optional(),
  q: z.string().optional(),
  limit: z.number().int().min(1).max(SPEC_DISTINCT_VALUES_MAX_LIMIT).optional(),
});

export type SpecDistinctValuesBrowserInput = z.infer<typeof specDistinctValuesInputSchema>;

// ---------------------------------------------------------------------------
// The details page (#693)
// ---------------------------------------------------------------------------

/**
 * A version's lines page: the source's constant 25 (`specificationsApi.ts:62`),
 * Python's `LINES_PAGE_SIZE`, pinned by the specifications drift test. The
 * infinite query and the `?lineId=` deep-link loop read it; the server never
 * takes a size from the browser.
 */
export const LINES_PAGE_SIZE = 25;

const recordId = z.number().int().min(1);

/** The spec summary's input by spec id — the details page's own read. */
export const specSummaryInputSchema = z.strictObject({
  specificationId: recordId,
});

export type SpecSummaryBrowserInput = z.infer<typeof specSummaryInputSchema>;

/** The spec summary's input by version id — the `/specs/:versionId/*` resolver's. */
export const specSummaryByVersionInputSchema = z.strictObject({
  versionId: recordId,
});

export type SpecSummaryByVersionBrowserInput = z.infer<typeof specSummaryByVersionInputSchema>;

/**
 * One lines page's input. `cursor` is the offset the previous page answered
 * with; `search` absent or empty is no term, anything else is matched as
 * typed — the page sends the debounced box value untrimmed, as the source does.
 * A later page also sends `heldLineIds`, the lines its view already holds, and
 * under a term `snapshotGroupIds`, the view's groups: the server leaves both
 * out of the page (#910). The first page sends neither.
 */
export const specLinesInputSchema = z.strictObject({
  specificationId: recordId,
  versionId: recordId,
  cursor: z.number().int().min(0),
  search: z.string().optional(),
  heldLineIds: z.array(recordId).optional(),
  snapshotGroupIds: z.array(recordId).optional(),
});

export type SpecLinesBrowserInput = z.infer<typeof specLinesInputSchema>;

/**
 * One page of the review step's lines (#950): the version's alive lines in
 * `statuses` — one list, at least one, each a line status; the dialog sends
 * `REVIEW_LINE_STATUSES` — from the offset the previous page answered with.
 * No term: the review reads afresh from its first page.
 */
export const specReviewLinesInputSchema = z.strictObject({
  specificationId: recordId,
  versionId: recordId,
  cursor: z.number().int().min(0),
  statuses: lineStatusesSchema,
});

export type SpecReviewLinesBrowserInput = z.infer<typeof specReviewLinesInputSchema>;

/**
 * A version of a spec — the Create Visualization dialog's two reads (#755), the
 * content tree and the vis-spec line ids, each of the spec's last published
 * version.
 */
export const specVersionRefInputSchema = z.strictObject({
  specificationId: recordId,
  versionId: recordId,
});

export type SpecVersionRefBrowserInput = z.infer<typeof specVersionRefInputSchema>;

/**
 * The dialog's Generate & Export (#1229; ADR-0030 decision 2): the version
 * prepare reached finalize with, the selections confirmed in review by line id
 * (`{}` when review was skipped), and the four header strings the browser
 * supplies, as Timmy does, printed verbatim into the workbook.
 */
export const specVisualizationExportInputSchema = z.strictObject({
  specificationId: recordId,
  versionId: recordId,
  selectedOptions: z.record(z.string(), z.array(recordId)),
  homePlan: z.string(),
  communityName: z.string(),
  communityFullId: z.string(),
  divisionName: z.string(),
});

export type SpecVisualizationExportBrowserInput = z.infer<
  typeof specVisualizationExportInputSchema
>;

// ---------------------------------------------------------------------------
// The lifecycle verbs (#707)
// ---------------------------------------------------------------------------
//
// What the dialogs (#696) may ask the BFF to write. Each list verb carries the
// page's own list query — the list read's browser input, verbatim — so the
// corrected page it answers with lands in exactly the cache key the page reads
// (ADR-0014 decision 4). The name, division and community-id rules are
// Python's: a refusal there is a tagged error the dialog can show, where a
// rejection here would be scrubbed to a generic message.

/** A spec from scratch: a core, or a base in a division. */
export const specCreateInputSchema = z.strictObject({
  type: z.enum(["core", "base"]),
  name: z.string(),
  divisionId: recordId.optional(),
});

export type SpecCreateBrowserInput = z.infer<typeof specCreateInputSchema>;

/** A new name, and on a community spec a new Community id. */
export const specRenameInputSchema = z.strictObject({
  specId: recordId,
  name: z.string(),
  communityId: z.string().optional(),
  listQuery: specListQueryInputSchema,
});

export type SpecRenameBrowserInput = z.infer<typeof specRenameInputSchema>;

/** Archive (`true`) or Enable (`false`). */
export const specSetArchivedInputSchema = z.strictObject({
  specId: recordId,
  archived: z.boolean(),
  listQuery: specListQueryInputSchema,
});

export type SpecSetArchivedBrowserInput = z.infer<typeof specSetArchivedInputSchema>;

/** Unlink core (#1085): the base, and the page to answer with. */
export const specUnlinkCoreInputSchema = z.strictObject({
  specId: recordId,
  listQuery: specListQueryInputSchema,
});

export type SpecUnlinkCoreBrowserInput = z.infer<typeof specUnlinkCoreInputSchema>;

/** The version the dialog captioned, or `null` for a Shell's own row. */
export const specDeleteVersionInputSchema = z.strictObject({
  specId: recordId,
  versionId: recordId.nullable(),
  listQuery: specListQueryInputSchema,
});

export type SpecDeleteVersionBrowserInput = z.infer<typeof specDeleteVersionInputSchema>;

// ---------------------------------------------------------------------------
// The version cloner (#804)
// ---------------------------------------------------------------------------
//
// What #812's dialogs may ask the BFF to copy (ADR-0017). Each answers with the
// new draft's ids and its spec summary, and its hook lands the user on the
// draft's edit route. The name, division, community-id and parent rules are
// Python's, so a refusal is a tagged error rather than a scrubbed rejection
// here. The two creates are two inputs over one wire, the type fixed by which
// one the caller names.

/** Create Version: a new draft of the spec. */
export const specCreateVersionInputSchema = z.strictObject({
  specId: recordId,
});

export type SpecCreateVersionBrowserInput = z.infer<typeof specCreateVersionInputSchema>;

/** Duplicate the version the dialog's `Based on:` line names. A base sends
 *  its division, a community its Community ID — each the source's when left
 *  out. */
export const specDuplicateInputSchema = z.strictObject({
  specId: recordId,
  versionId: recordId,
  name: z.string(),
  divisionId: recordId.optional(),
  communityId: z.string().optional(),
});

export type SpecDuplicateBrowserInput = z.infer<typeof specDuplicateInputSchema>;

/** A base from a core version, in a division. */
export const specCreateBaseFromCoreInputSchema = z.strictObject({
  basedOnVersionId: recordId,
  name: z.string(),
  divisionId: recordId,
});

export type SpecCreateBaseFromCoreBrowserInput = z.infer<typeof specCreateBaseFromCoreInputSchema>;

/** A community from a base version, for a Community ID; its division is the
 *  base's, taken server-side. The community page's create-from-community
 *  sends this too. */
export const specCreateCommunityFromBaseInputSchema = z.strictObject({
  basedOnVersionId: recordId,
  name: z.string(),
  communityId: z.string(),
});

export type SpecCreateCommunityFromBaseBrowserInput = z.infer<
  typeof specCreateCommunityFromBaseInputSchema
>;

// ---------------------------------------------------------------------------
// Publish (#949)
// ---------------------------------------------------------------------------
//
// What the Review & Publish dialog (#951) may ask the BFF to publish. The two
// limits are the dialog's own (the donor's `MAX_VERSION_NAME_CHARACTERS` and
// `MAX_PUBLISH_NOTE_CHARACTERS`), shared with its form, and Python enforces
// them again; an over-limit request cannot come from the fixed dialog, so its
// rejection here is the generic error. Clearing an empty field, and the two refusals
// the dialog shows, are Python's.

export const SPEC_VERSION_NAME_MAX_LENGTH = 100;
export const SPEC_PUBLISHING_NOTE_MAX_LENGTH = 500;

/** Publish a version: the picked day's instant (`Date.toISOString()` sends it
 *  with a `Z`), and the name and note as typed. */
export const specPublishInputSchema = z.strictObject({
  specificationId: recordId,
  versionId: recordId,
  effectiveAt: z.iso.datetime({ offset: true }),
  name: z.string().max(SPEC_VERSION_NAME_MAX_LENGTH).nullable(),
  publishingNote: z.string().max(SPEC_PUBLISHING_NOTE_MAX_LENGTH).nullable(),
});

export type SpecPublishBrowserInput = z.infer<typeof specPublishInputSchema>;

/**
 * The over-target reads' input (#1215 over #1214's wire): the spec, whose
 * latest version the server counts on. The Spec list's Over Target popover
 * reads its selections with it, and "Where to save" (#1217) takes the same.
 */
export const specOverTargetInputSchema = z.strictObject({
  specificationId: recordId,
});

export type SpecOverTargetBrowserInput = z.infer<typeof specOverTargetInputSchema>;

/** The linked-communities read (#1088): the base and the version being pushed. */
export const specLinkedCommunitiesInputSchema = z.strictObject({
  specificationId: recordId,
  versionId: recordId,
});

export type SpecLinkedCommunitiesBrowserInput = z.infer<typeof specLinkedCommunitiesInputSchema>;

/**
 * The push preview (#1090): the base, the version pushed (in step 3 the
 * draft about to publish), the community, and the list asked for.
 */
export const specPushPreviewInputSchema = z.strictObject({
  specificationId: recordId,
  versionId: recordId,
  communityId: recordId,
  tab: z.enum(["published", "draft"]),
});

export type SpecPushPreviewBrowserInput = z.infer<typeof specPushPreviewInputSchema>;

/**
 * Start a batch push (#1088 over #1143's start): the picked ids in pick order,
 * at most `SPEC_PUSH_COMMUNITY_IDS_MAX`, the Effective date's instant, and —
 * from Review & Publish's first click only — the base version's name and note;
 * `null` from Retry and Push Changes, which never publish. The server refuses
 * an empty or duplicated list itself.
 */
export const specPushStartInputSchema = z.strictObject({
  specificationId: recordId,
  versionId: recordId,
  communityIds: z.array(recordId).max(SPEC_PUSH_COMMUNITY_IDS_MAX),
  effectiveAt: z.iso.datetime({ offset: true }),
  publish: z
    .strictObject({
      versionName: z.string().max(SPEC_VERSION_NAME_MAX_LENGTH).nullable(),
      publishingNote: z.string().max(SPEC_PUBLISHING_NOTE_MAX_LENGTH).nullable(),
    })
    .nullable(),
});

export type SpecPushStartBrowserInput = z.infer<typeof specPushStartInputSchema>;

/** A push's progress check, by the id its start answered. */
export const specPushProgressInputSchema = z.strictObject({
  pushId: z.uuid(),
});

export type SpecPushProgressBrowserInput = z.infer<typeof specPushProgressInputSchema>;

/**
 * The details route's search params (#695): the line search, and the Over
 * Target popover's deep link to one selection. The page writes the term and
 * never the ids. Anything unreadable drops to absent rather than failing the
 * navigation, as the list route's schema does.
 */
export const specDetailsSearchSchema = z.object({
  specSearch: z.string().optional().catch(undefined),
  // The raw text, as the donor keeps it; `deepLinkId` reads it where it is used.
  lineId: z.string().optional().catch(undefined),
  selectionId: z.string().optional().catch(undefined),
});

export type SpecDetailsSearch = z.infer<typeof specDetailsSearchSchema>;

/** A URL value the router may have JSON-parsed into a number or a boolean, read as its text. */
const urlText = z.union([z.string(), z.number(), z.boolean()]).pipe(z.coerce.string());

/** Space-separated ids, a lone one arriving as a number; anything else dropped. */
const idListParam = z
  .union([z.string(), z.number()])
  .pipe(z.coerce.string())
  .optional()
  .catch(undefined);

/**
 * The edit route's search params: the Editor's line search (#806) — no
 * deep-link poller, so no `lineId` / `selectionId` — and beside it the Item
 * Search island's own keys (#811, the donor's `SEARCH_FILTERS_QUERY_PARAMS`,
 * `ItemSearchIsland/constants.ts`). Each pane writes only its own keys and
 * merges into the rest, as the donor's functional `setSearchParams` does.
 *
 *   - `itemSearch` — the island's term, plain text (`@/lib/router-search-codec`);
 *   - `category` — a category slug, the island's category filter;
 *   - `mfrs`, `tiers` — space-separated ids (a lone id arrives as a number);
 *   - `status` — never written or sent by the island: only the empty-category
 *     self-heal reads it, so a link carrying it keeps its category (#767 R1).
 *
 *   - `page` — never read (#767 R1), but declared so it stays in the URL
 *     through the next write, as the donor's functional `setSearchParams`
 *     keeps every param: the router drops an undeclared key from the typed
 *     search, and with it from the URL the next navigation writes.
 *
 * Anything unreadable drops to absent, one key at a time.
 */
export const specEditSearchSchema = z.object({
  specSearch: z.string().optional().catch(undefined),
  itemSearch: z.string().optional().catch(undefined),
  category: urlText.optional().catch(undefined),
  mfrs: idListParam,
  tiers: idListParam,
  status: urlText.optional().catch(undefined),
  page: urlText.optional().catch(undefined),
});

export type SpecEditSearch = z.infer<typeof specEditSearchSchema>;
